import React, { useState } from 'react';
import { 
  Container, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  Box,
  ThemeProvider,
  createTheme,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import logo from './logo.png';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2196F3',
    },
  },
  typography: {
    fontFamily: "'Segoe UI', 'Roboto', 'Arial', sans-serif",
  },
});

const translations = {
  en: {
    title: 'Compound Interest Calculator',
    yearlyInvestment: 'Yearly Investment Amount ($)',
    investmentDuration: 'Investment Duration (years)',
    waitingDuration: 'Waiting Duration (years)',
    finalAmount: 'Final Amount ($)',
    calculate: 'Calculate',
    result: 'Average Compound Interest Per Annum',
    language: 'Language'
  },
  zhCN: {
    title: '复利计算器',
    yearlyInvestment: '每年缴付金额 ($)',
    investmentDuration: '投资年期 (年)',
    waitingDuration: '等候年期 (年)',
    finalAmount: '总额 ($)',
    calculate: '计算',
    result: '年均复利率',
    language: '语言'
  },
  zhTW: {
    title: '複利計算器',
    yearlyInvestment: '每年繳付金額 ($)',
    investmentDuration: '投資年期 (年)',
    waitingDuration: '等候年期 (年)',
    finalAmount: '總額 ($)',
    calculate: '計算',
    result: '年均複利率',
    language: '語言'
  }
};

function App() {
  const [investment, setInvestment] = useState('');
  const [duration, setDuration] = useState('');
  const [waitingPeriod, setWaitingPeriod] = useState('');
  const [finalAmount, setFinalAmount] = useState('');
  const [result, setResult] = useState(null);
  const [language, setLanguage] = useState('en');

  const t = translations[language];

  const calculateTotalAmount = (yearlyInvestment, investmentYears, waitingYears, rate) => {
    let total = 0;

    // For each investment year
    for (let year = 0; year < investmentYears; year++) {
      // Calculate remaining years until the end (including waiting period)
      const remainingYears = waitingYears + (investmentYears - year);
      
      // Calculate future value of this year's investment
      const futureValue = yearlyInvestment * Math.pow(1 + rate, remainingYears);
      total += futureValue;
    }

    return total;
  };

  const calculateCompoundInterest = () => {
    // Convert inputs to numbers
    const yearlyInvestment = parseFloat(investment);
    const investmentYears = parseFloat(duration);
    const waitingYears = parseFloat(waitingPeriod);
    const targetAmount = parseFloat(finalAmount);

    if (isNaN(yearlyInvestment) || isNaN(investmentYears) || isNaN(waitingYears) || isNaN(targetAmount)) {
      setResult('Please enter valid numbers');
      return;
    }

    // Initialize binary search parameters
    let low = 0;      // 0%
    let high = 2.0;   // 200%
    let epsilon = 0.0000001;
    let maxIterations = 1000;
    let bestRate = null;
    let bestDiff = Infinity;

    // Binary search for the interest rate
    for (let i = 0; i < maxIterations; i++) {
      const mid = (low + high) / 2;
      const total = calculateTotalAmount(yearlyInvestment, investmentYears, waitingYears, mid);
      const diff = Math.abs(total - targetAmount);
      
      // Keep track of best result
      if (diff < bestDiff) {
        bestDiff = diff;
        bestRate = mid;
      }

      // Check if we've found a close enough match
      if (diff < epsilon * targetAmount) {
        const rate = (mid * 100).toFixed(2);
        setResult(`${rate}%`);
        return;
      }

      // Adjust search range
      if (total < targetAmount) {
        low = mid;
      } else {
        high = mid;
      }
    }

    // If we didn't find an exact match, return the best approximation
    const rate = (bestRate * 100).toFixed(2);
    setResult(`${rate}% (approximate)`);
  };

  const handleLanguageChange = (event, newLanguage) => {
    if (newLanguage !== null) {
      setLanguage(newLanguage);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3
            }}>
              <img 
                src={logo} 
                alt="Mathless Logo" 
                style={{ 
                  height: '50px',
                  marginRight: '10px'
                }} 
              />
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ 
                  color: '#2196F3',
                  fontWeight: 500,
                  fontSize: '2.5rem',
                  lineHeight: 1,
                  fontFamily: "'Segoe UI', 'Roboto', 'Arial', sans-serif",
                }}
              >
                Mathless
              </Typography>
            </Box>
            <Typography 
              variant="h6" 
              component="h2" 
              align="center" 
              sx={{ 
                color: 'text.secondary',
                fontWeight: 400
              }}
            >
              {t.title}
            </Typography>
          </Box>
          
          <Box component="form" sx={{ '& .MuiTextField-root': { mb: 2 } }}>
            <TextField
              fullWidth
              label={t.yearlyInvestment}
              value={investment}
              onChange={(e) => setInvestment(e.target.value)}
              type="number"
              variant="outlined"
            />
            
            <TextField
              fullWidth
              label={t.investmentDuration}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              type="number"
              variant="outlined"
            />
            
            <TextField
              fullWidth
              label={t.waitingDuration}
              value={waitingPeriod}
              onChange={(e) => setWaitingPeriod(e.target.value)}
              type="number"
              variant="outlined"
            />
            
            <TextField
              fullWidth
              label={t.finalAmount}
              value={finalAmount}
              onChange={(e) => setFinalAmount(e.target.value)}
              type="number"
              variant="outlined"
            />
            
            <Button 
              fullWidth
              variant="contained" 
              onClick={calculateCompoundInterest}
              size="large"
              sx={{ mt: 2, py: 1.5 }}
            >
              {t.calculate}
            </Button>
          </Box>
          
          {result && (
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom color="text.secondary">
                {t.result}
              </Typography>
              <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                {result}
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={language}
              exclusive
              onChange={handleLanguageChange}
              aria-label={t.language}
            >
              <ToggleButton value="en" aria-label="English">
                English
              </ToggleButton>
              <ToggleButton value="zhCN" aria-label="简体中文">
                简体中文
              </ToggleButton>
              <ToggleButton value="zhTW" aria-label="繁體中文">
                繁體中文
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Paper>
      </Container>
    </ThemeProvider>
  );
}

export default App;
